import { API } from '@/config/api';
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, TrendingUp, AlertCircle, Lightbulb, Target } from 'lucide-react';


export default function InsightsDashboard() {
  const { user } = useOutletContext();
  const [analytics, setAnalytics] = useState(null);
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);

  const isPremium = user?.subscription_status && user.subscription_status !== 'free';

  useEffect(() => {
    if (isPremium) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [isPremium]);

  const fetchData = async () => {
    try {
      const [analyticsRes, streakRes] = await Promise.all([
        fetch(`${API}/analytics/overview`, { credentials: 'include' }),
        fetch(`${API}/profile/streak`, { credentials: 'include' })
      ]);

      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      if (streakRes.ok) setStreak(await streakRes.json());
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateInsights = () => {
    if (!analytics || analytics.total_doses === 0) return [];

    const insights = [];

    // Adherence insights
    const adherence = analytics.adherence_rate.rate;
    if (adherence >= 90) {
      insights.push({
        type: 'success',
        icon: Target,
        title: 'Excellent Adherence!',
        description: `You're maintaining ${adherence}% adherence to your protocols. This consistency maximizes research outcomes.`,
        action: null
      });
    } else if (adherence < 75) {
      insights.push({
        type: 'warning',
        icon: AlertCircle,
        title: 'Adherence Needs Attention',
        description: `Your adherence is ${adherence}%. Consider setting more reminders or simplifying your protocol schedule.`,
        action: 'View Scheduler'
      });
    }

    // Consistency insights
    const inconsistentPeptides = Object.entries(analytics.dose_consistency)
      .filter(([_, data]) => data.consistency_score < 70);
    
    if (inconsistentPeptides.length > 0) {
      insights.push({
        type: 'info',
        icon: Lightbulb,
        title: 'Dose Variation Detected',
        description: `${inconsistentPeptides[0][0]} shows high dose variation. Use the calculator's saved calculations for more consistent dosing.`,
        action: 'Open Calculator'
      });
    }

    // Cost optimization
    if (analytics.cost_optimization.total_cost > 0) {
      const avgCost = analytics.cost_optimization.average_cost_per_dose;
      insights.push({
        type: 'info',
        icon: TrendingUp,
        title: 'Cost Efficiency',
        description: `Your average cost per dose is £${avgCost.toFixed(2)}. Consider bulk purchasing or comparing suppliers to optimize costs.`,
        action: 'View Suppliers'
      });
    }

    // Streak insights
    if (streak && streak.current_streak >= 30) {
      insights.push({
        type: 'success',
        icon: Sparkles,
        title: '30-Day Streak Achievement!',
        description: `Incredible dedication! You've maintained a ${streak.current_streak}-day streak. Consistency is key in research.`,
        action: null
      });
    }

    // Protocol effectiveness
    const effectiveProtocols = Object.entries(analytics.protocol_effectiveness)
      .filter(([_, data]) => data.effectiveness_score >= 80);
    
    if (effectiveProtocols.length > 0) {
      insights.push({
        type: 'success',
        icon: Target,
        title: 'High Effectiveness Protocols',
        description: `${effectiveProtocols[0][0]} is showing excellent results with minimal side effects (${effectiveProtocols[0][1].effectiveness_score}/100 score).`,
        action: null
      });
    }

    return insights;
  };

  if (loading) {
    return <div className="p-8 text-center">Loading insights...</div>;
  }

  if (!isPremium) {
    return (
      <div className="p-4 md:p-8 max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold mb-8">Insights Dashboard</h1>
        <Card className="p-12 text-center glass-card">
          <Sparkles className="h-16 w-16 mx-auto mb-4 text-primary" />
          <h2 className="text-2xl font-bold mb-4">Premium Feature</h2>
          <p className="text-muted-foreground mb-6">Get AI-powered insights and recommendations</p>
          <Button size="lg">Upgrade to Premium</Button>
        </Card>
      </div>
    );
  }

  const insights = generateInsights();

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto" data-testid="insights-page">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">
          AI Insights Dashboard
        </h1>
        <p className="text-muted-foreground">
          Smart recommendations based on your research data
        </p>
      </div>

      {insights.length === 0 ? (
        <Card className="p-12 text-center glass-card">
          <Sparkles className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">
            Keep logging doses to generate personalized insights
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {insights.map((insight, index) => {
            const Icon = insight.icon;
            const colorClass = insight.type === 'success' ? 'border-success/50 bg-success/5' :
                              insight.type === 'warning' ? 'border-warning/50 bg-warning/5' :
                              'border-info/50 bg-info/5';
            const iconColor = insight.type === 'success' ? 'text-success' :
                             insight.type === 'warning' ? 'text-warning' :
                             'text-info';

            return (
              <Card key={index} className={`p-6 glass-card ${colorClass}`}>
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg bg-background/50`}>
                    <Icon className={`h-6 w-6 ${iconColor}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-2">{insight.title}</h3>
                    <p className="text-muted-foreground mb-4">{insight.description}</p>
                    {insight.action && (
                      <Button variant="outline" size="sm">
                        {insight.action}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Quick Stats */}
      {analytics && analytics.total_doses > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          <Card className="p-6 glass-card text-center">
            <p className="text-4xl font-bold text-primary mb-2">
              {analytics.adherence_rate.rate}%
            </p>
            <p className="text-sm text-muted-foreground">Adherence Score</p>
          </Card>
          <Card className="p-6 glass-card text-center">
            <p className="text-4xl font-bold text-success mb-2">
              {Object.keys(analytics.dose_consistency).length}
            </p>
            <p className="text-sm text-muted-foreground">Active Peptides</p>
          </Card>
          <Card className="p-6 glass-card text-center">
            <p className="text-4xl font-bold text-info mb-2">
              {streak?.current_streak || 0}
            </p>
            <p className="text-sm text-muted-foreground">Day Streak</p>
          </Card>
        </div>
      )}
    </div>
  );
}
